import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { Controller, useForm } from 'react-hook-form'
import SydLabel, { hookFormErrorAdapter } from '../../../../../commonDesign/SydLabel'
import SydButton from '../../../../../commonDesign/Button'
import { useDialogStyles } from '../../useDialogStyles'
import SydInput from '../../../../../commonDesign/SydInput'
import { useModifyAccountDisplayNumberMutation } from '../../../../../../api/accounts'
import Alert from '../../../../../atoms/Alert'
import { ALERT_SEVERITY, ALERT_VARIANT } from '../../../../../../constants'

const useSubmitter = (form, onComplete) => {
  const { handleSubmit, clearErrors } = form
  const mutation = useModifyAccountDisplayNumberMutation()
  const onSubmit = useCallback(async (formData) => {
    const command = {
      accountId: formData.accountId,
      displayNumber: formData.displayNumber || null
    }

    try {
      form.clearErrors()
      const result = await mutation.mutateAsync(command)
      onComplete(result)
    } catch (err) {
      form.setError('root', {
        type: 'custom',
        message: err.message || 'An error occurred while updating the display number.'
      })
    }
  }, [form, mutation, onComplete])

  const submitter = useCallback(async (e) => {
    const onValid = async (form) => {
      await onSubmit(form)
    }
    const onInvalid = (errors) => {
      console.error(errors)
    }
    clearErrors()
    const handler = handleSubmit(onValid, onInvalid)
    await handler(e)
  }, [handleSubmit, clearErrors, onSubmit])

  return {
    submitter,
    processing: mutation.isLoading
  }
}

function DisplayNumberForm ({ account, onCancel, onComplete }) {
  const classes = useDialogStyles()
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      accountId: account.accountId,
      displayNumber: account.displayNumber
    }
  })

  const { submitter, processing } = useSubmitter(form, onComplete)

  return (
    <>
      <div className={classes.form}>
        {form.formState.errors.root && (
          <Alert variant={ALERT_VARIANT.standard} severity={ALERT_SEVERITY.error}>
            {form.formState.errors.root.message}
          </Alert>
        )}
        <div className={classes.group}>
          <Controller
            name='displayNumber'
            control={form.control}
            rules={{ maxLength: { value: 80, message: 'Max 80 Characters' } }}
            render={({ field, fieldState }) => (
              <SydLabel
                label='Account Display Number'
                description='Max 80 Characters'
                error={hookFormErrorAdapter(form, fieldState)}
              >
                <SydInput placeholder='Enter a Display Number' {...field} />
              </SydLabel>
            )}
          />
        </div>
      </div>
      <div className={classes.actions}>
        <SydButton disabled={processing} variant='ghost' size='lg' onClick={onCancel}>Cancel</SydButton>
        <SydButton
          variant='primary'
          size='lg'
          onClick={submitter}
          processing={processing}
          disabled={!form.formState.isValid}
        >
          Save
        </SydButton>
      </div>
    </>
  )
}

DisplayNumberForm.propTypes = {
  account: PropTypes.object,
  onComplete: PropTypes.func,
  onCancel: PropTypes.func
}

export default DisplayNumberForm
